import { Injectable } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

/** Marker / context type previously from ngx-modialog-7. */
export class ModalContext {
  [key: string]: any;
}

/** Bootstrap modal context previously from ngx-modialog-7/plugins/bootstrap. */
export class BSModalContext extends ModalContext {}

export interface ModalComponent<T = ModalContext> {}

export interface CloseGuard {
  beforeDismiss?(): boolean | Promise<boolean>;
  beforeClose?(): boolean | Promise<boolean>;
}

/**
 * Pass-through for legacy ngx-modialog overlayConfigFactory(config, ContextType).
 * Returns the config object used as dialog context + size options.
 */
export function overlayConfigFactory(config: any = {}, _contextType?: any): any {
  return { ...config };
}

/**
 * DialogRef-compatible wrapper over BsModalRef so existing popups can keep
 * injecting DialogRef and calling dialog.close() / reading dialog.context.
 */
export class DialogRef<T = any> {
  context: T;
  result: Promise<any>;
  overlayRef = {
    location: {
      nativeElement: null as HTMLElement | null,
    },
  };

  private bsModalRef: BsModalRef | null = null;
  private settled = false;
  private resolveFn!: (value: any) => void;
  private rejectFn!: (reason?: any) => void;

  constructor(context: T) {
    this.context = context;
    this.result = new Promise((resolve, reject) => {
      this.resolveFn = resolve;
      this.rejectFn = reject;
    });
    // Avoid unhandled rejection if caller never attaches .then()
    this.result.catch(() => undefined);
  }

  attach(bsModalRef: BsModalRef): void {
    this.bsModalRef = bsModalRef;
    setTimeout(() => {
      const containers = document.querySelectorAll('modal-container');
      const el = containers.length
        ? (containers[containers.length - 1] as HTMLElement)
        : null;
      this.overlayRef.location.nativeElement = el;
    });

    if (bsModalRef.onHidden) {
      bsModalRef.onHidden.subscribe(() => {
        if (!this.settled) {
          this.settle(undefined);
        }
      });
    }
  }

  close(result?: any, _delayMs?: number): void {
    this.settle(result);
    if (this.bsModalRef) {
      this.bsModalRef.hide();
    }
  }

  dismiss(reason?: any): void {
    this.settle(reason);
    if (this.bsModalRef) {
      this.bsModalRef.hide();
    }
  }

  destroy(): void {
    this.close(undefined);
  }

  private settle(value: any): void {
    if (this.settled) {
      return;
    }
    this.settled = true;
    this.resolveFn(value);
  }
}

/**
 * Drop-in replacement for ngx-modialog-7 Modal service, backed by ngx-bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class Modal {
  constructor(private bsModalService: BsModalService) {}

  open(component: any, config: any = {}): DialogRef<any> {
    const context = { ...(config || {}) };
    const dialogClass: string = context.dialogClass || '';
    delete context.dialogClass;

    // ngx-modialog used dialogClass like "modal-dialog modal-8";
    // ngx-bootstrap `class` is appended onto modal-dialog.
    const bsClass = dialogClass
      .split(/\s+/)
      .filter((c: string) => c && c !== 'modal-dialog')
      .join(' ');

    const size = context.size;
    delete context.size;
    const sizeClass =
      size === 'sm' ? 'modal-sm' : size === 'lg' ? 'modal-lg' : size === 'xl' ? 'modal-xl' : '';

    const dialogRef = new DialogRef(context);

    const bsModalRef = this.bsModalService.show(component, {
      class: [bsClass, sizeClass].filter(Boolean).join(' '),
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      animated: true,
      providers: [{ provide: DialogRef, useValue: dialogRef }],
    });

    dialogRef.attach(bsModalRef);
    return dialogRef;
  }
}
